import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchEventTriggeredFlowsPaginated, FLOW_STATUS } from '@/lib/api/email-flows';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import type { PaginatedFlowsResponse } from '@/lib/api/email-flows/types';

export interface EmailFlowOption {
    name: string;
    flow_id: number;
}

const PER_PAGE = 50; // Fetch more per page for dropdown to minimize requests

interface UseEmailFlowOptionsParams {
    /** Only fetch when enabled (e.g., when modal is open) */
    enabled?: boolean;
}

/**
 * Hook to get available email flows for adding customers
 * Returns only published flows
 *
 * This hook fetches ALL pages of flows when enabled, making it suitable
 * for dropdowns where all options need to be available.
 *
 * @param params.enabled - Only fetch when true (default: true)
 */
export function useEmailFlowOptions({ enabled = true }: UseEmailFlowOptionsParams = {}) {
    const query = useInfiniteQuery<PaginatedFlowsResponse, Error>({
        queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST, 'dropdown'],
        queryFn: ({ pageParam }) =>
            fetchEventTriggeredFlowsPaginated({
                page: pageParam as number,
                per_page: PER_PAGE,
            }),
        initialPageParam: 1,
        getNextPageParam: lastPage => {
            return lastPage.has_more ? lastPage.page + 1 : undefined;
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        meta: {
            error: {
                sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-flows-for-dropdown',
                feature: SENTRY_FEATURES.EMAIL_FLOWS,
            },
        },
    });

    // Auto-fetch all pages when enabled and there are more pages
    useEffect(() => {
        if (enabled && query.hasNextPage && !query.isFetchingNextPage) {
            query.fetchNextPage();
        }
    }, [enabled, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

    // Flatten all pages into single array
    const flows = query.data?.pages.flatMap(page => page.flows) ?? [];

    // Determine if we're still loading (initial load OR fetching more pages)
    const isLoading = query.isLoading || (query.isFetchingNextPage && query.hasNextPage);

    const availableFlows = useMemo((): EmailFlowOption[] => {
        if (!flows) return [];

        return flows
            .filter(flow => flow.status === FLOW_STATUS.PUBLISHED)
            .map(flow => ({
                name: flow.data?.name || 'Unnamed Flow',
                flow_id: flow.flow_id,
            }));
    }, [flows]);

    const dropdownOptions = useMemo((): DropdownMenuOption[] => {
        return availableFlows.map(flow => ({
            label: flow.name,
            value: String(flow.flow_id),
        }));
    }, [availableFlows]);

    const findFlowById = (flowId: string): EmailFlowOption | undefined => {
        return availableFlows.find(f => String(f.flow_id) === flowId);
    };

    return {
        availableFlows,
        dropdownOptions,
        findFlowById,
        isLoading,
    };
}
